import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Shield, Globe, Code2, Clock, UserCheck, Zap } from 'lucide-react';
import PageTransition from '../components/ui/PageTransition';
import SectionHeading from '../components/ui/SectionHeading';
import Button from '../components/ui/Button';
import HeroSection from '../components/sections/HeroSection';
import CtaSection from '../components/sections/CtaSection';
import TestimonialCard from '../components/ui/TestimonialCard';
import { testimonials } from '../data/testimonials';

const WhyChooseUsPage = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Why Choose Us | Consiliuma Web Design & Development</title>
        <meta name="description" content="Discover why businesses choose Consiliuma for premium web design and development. British expertise, global reach, and exceptional results." />
        <meta name="keywords" content="Best Web Design Nottingham, Top Web Developers UK, Web Development Agency Nottingham, Full Stack Agency UK" />
        <link rel="canonical" href="/why-choose-us" />
      </Helmet>

      <HeroSection
        title="Why Choose Consiliuma"
        subtitle="British expertise delivering exceptional digital experiences worldwide"
        ctaText="Start Your Project"
        ctaLink="/quote"
        backgroundImage="https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />

      {/* Key Benefits */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <SectionHeading 
            title="Our Unique Advantages" 
            subtitle="What sets us apart from other web development agencies"
            center
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md border-t-4 border-primary"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Shield size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">British Expertise</h3>
              <p className="text-secondary">
                Our all-British development team brings exceptional quality standards, attention to detail, and technical expertise to every project. We pride ourselves on clear communication and a thorough understanding of both local and international business needs.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md border-t-4 border-primary"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Globe size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Global Reach</h3>
              <p className="text-secondary">
                With offices in Nottingham, Arizona, and Southeast Asia, we serve clients worldwide with a deep understanding of diverse markets. Our international presence allows us to provide round-the-clock support and incorporate global best practices in our work.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md border-t-4 border-primary"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Code2 size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Full-Stack Capabilities</h3>
              <p className="text-secondary">
                Our comprehensive expertise spans front-end design to back-end development, ensuring seamless integration and consistent quality throughout your project. This full-service approach eliminates the complexity of managing multiple vendors.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md border-t-4 border-primary"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Clock size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Long-term Support</h3>
              <p className="text-secondary">
                We believe in building lasting relationships with our clients. Our comprehensive maintenance services ensure your website remains secure, up-to-date, and performing optimally long after launch, providing peace of mind and ongoing value.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md border-t-4 border-primary"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <UserCheck size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Client-Focused Approach</h3>
              <p className="text-secondary">
                We take the time to understand your business goals, target audience, and specific requirements. This client-centered approach ensures we deliver solutions that not only look great but also achieve your business objectives and provide ROI.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-lg shadow-md border-t-4 border-primary"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">
                <Zap size={40} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-primary">Results-Driven Solutions</h3>
              <p className="text-secondary">
                Every website we build is optimized for performance, user experience, and SEO. We focus on creating solutions that not only impress visually but also drive business results through improved conversion rates, higher search rankings, and enhanced user engagement.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-background-alt">
        <div className="container-custom">
          <SectionHeading 
            title="What Our Clients Say" 
            subtitle="Hear from businesses we've helped succeed online"
            center
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <TestimonialCard
                  name={testimonial.name}
                  company={testimonial.company}
                  text={testimonial.text}
                  location={testimonial.location}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <SectionHeading
                title="Our Approach"
                subtitle="How we deliver exceptional results"
              />
              <p className="mb-6">
                At Consiliuma, we follow a structured yet flexible approach that ensures quality, efficiency, and client satisfaction throughout the development process.
              </p>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold mb-2 text-primary">Discovery & Planning</h3>
                  <p className="text-secondary">
                    We begin by understanding your business, goals, and target audience. This comprehensive discovery phase ensures our solution aligns perfectly with your vision and objectives.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold mb-2 text-primary">Collaborative Design</h3>
                  <p className="text-secondary">
                    Our design process involves close collaboration with you at every stage. We create prototypes and mockups for feedback, ensuring the final design meets your expectations.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold mb-2 text-primary">Transparent Development</h3>
                  <p className="text-secondary">
                    We keep you informed throughout the development process with regular updates and milestone reviews, so you always know where your project stands.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold mb-2 text-primary">Rigorous Testing</h3>
                  <p className="text-secondary">
                    Every website undergoes thorough testing across devices, browsers, and user scenarios to ensure flawless performance, security, and user experience.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold mb-2 text-primary">Comprehensive Support</h3>
                  <p className="text-secondary">
                    Our relationship continues after launch with ongoing maintenance, updates, and support to ensure your website remains secure, up-to-date, and performing optimally.
                  </p>
                </div>
              </div>
              
              <div className="mt-8">
                <Button to="/our-process" variant="outline">
                  Learn More About Our Process
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://images.pexels.com/photos/3182759/pexels-photo-3182759.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
                alt="Consiliuma team working on web development" 
                className="rounded-lg shadow-lg w-full"
              />
              <div className="absolute -bottom-8 -left-8 bg-white p-6 rounded-lg shadow-lg border-l-4 border-primary max-w-xs">
                <p className="text-lg font-semibold text-primary">
                  "We're committed to delivering not just websites, but business solutions that drive real results for our clients."
                </p>
                <p className="text-sm text-gray-600 mt-2">— Consiliuma Team</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
            >
              <p className="text-4xl lg:text-5xl font-bold mb-2">100+</p>
              <p className="text-xl">Projects Completed</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <p className="text-4xl lg:text-5xl font-bold mb-2">15+</p>
              <p className="text-xl">Years Experience</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <p className="text-4xl lg:text-5xl font-bold mb-2">3</p>
              <p className="text-xl">Global Offices</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <p className="text-4xl lg:text-5xl font-bold mb-2">98%</p>
              <p className="text-xl">Client Satisfaction</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        title="Ready to Transform Your Online Presence?"
        subtitle="Let's discuss how our British expertise can help your business succeed online."
        buttonText="Get a Free Quote"
        buttonLink="/quote"
      />
    </PageTransition>
  );
};

export default WhyChooseUsPage;